import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import SidePanel from 'components/application/SidePanel';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import CheckboxFilter from 'components/filters/CheckboxFilter';
import DateFilter from 'components/filters/DateFilter';
import MultiSelectFilter from 'components/filters/MultiSelectFilter';
import SelectFilter from 'components/filters/SelectFilter';
import { dashboardConfigurableFieldKey } from 'components/projectsDashboard/helpers';
import { buildBaseProjectFilters } from 'components/projectsDashboard/hooks/useProjectFilters';

import { personDisplayName } from 'components/helpers/users';

export default function ProjectsFilterSidePanel({
  closeSidePanel,
  filters,
  isSidePanelOpen,
  onFilterAdd,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueChange,
  onFilterValueRemove,
  visibleFilters,
}) {
  const currentActor = useCurrentActor();

  const { seedDashboardConfigurableFields } = useProjectsDashboardContext();

  const isInitialMount = useRef(true);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.ProjectsFilterSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { pluralRAMSName, singularRAMSName } = currentActor.division.attributes;

  const baseFilters = buildBaseProjectFilters({
    dashboardConfigurableFields: seedDashboardConfigurableFields.data,
    translations: {
      approvalStatus: i18nTCommon('projects.approvalStatus'),
      approver: i18nTCommon('projects.approver'),
      creationDate: i18nTCommon('shared.creationDate'),
      creator: i18nTCommon('projects.creator'),
      folder: i18nTCommon('projects.folder'),
      lastUpdated: i18nTCommon('shared.lastUpdated'),
      lifecycleStatus: i18nTCommon('projects.projectStatus', {
        interpolation: { escapeValue: false },
        singularRAMSName: voca.capitalize(singularRAMSName),
      }),
      reviewDate: i18nTCommon('projects.reviewDate'),
      reviewer: i18nTCommon('projects.reviewer'),
      reviewStatus: i18nTCommon('projects.reviewStatus'),
      signatureStatus: i18nTCommon('projects.signatureStatus'),
      startDate: i18nTCommon('shared.startDate'),
    },
  });

  const selectedVisibleFilters = filters.selectedFilters.filter((filterKey) =>
    visibleFilters.includes(filterKey),
  );
  const isAddFilterDisabled = visibleFilters.every((filterKey) =>
    selectedVisibleFilters.includes(filterKey),
  );

  const headerContent = (
    <div className='tw-relative tw-flex tw-justify-between'>
      <h1 className='tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
        {i18nT('title', {
          interpolation: { escapeValue: false },
          pluralRAMSName,
        })}
      </h1>
      <div
        className="tw-absolute tw-right-0 tw-top-[5px] tw-h-[18px] tw-w-[18px] tw-cursor-pointer before:tw-absolute before:tw-left-2 before:tw-h-[18px] before:tw-w-[2px] before:tw-rotate-45 before:tw-bg-grey-500 before:tw-content-[''] after:tw-absolute after:tw-left-2 after:tw-h-[18px] after:tw-w-[2px] after:-tw-rotate-45 after:tw-bg-grey-500 after:tw-content-['']"
        data-element-name='side-panel-close'
        onClick={closeSidePanel}
      />
    </div>
  );

  const autoFocus = !isInitialMount.current;

  const filterComponents = {
    lifecycleStatusFilter: (
      <LifecycleStatusFilter
        autoFocus={autoFocus}
        filter={filters.lifecycleStatusFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
      />
    ),
    folderFilter: (
      <FolderFilter
        autoFocus={autoFocus}
        filter={filters.folderFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueAdd={onFilterValueAdd}
        onFilterValueRemove={onFilterValueRemove}
      />
    ),
    reviewStatusFilter: (
      <ReviewStatusFilter
        autoFocus={autoFocus}
        filter={filters.reviewStatusFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
      />
    ),
    reviewDateFilter: (
      <ReviewDateFilter
        autoFocus={autoFocus}
        filter={filters.reviewDateFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
        validationErrors={filters.validationErrors.reviewDateFilter}
      />
    ),
    lastUpdatedFilter: (
      <LastUpdatedFilter
        autoFocus={autoFocus}
        filter={filters.lastUpdatedFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
        validationErrors={filters.validationErrors.lastUpdatedFilter}
      />
    ),
    approvalStatusFilter: (
      <ApprovalStatusFilter
        autoFocus={autoFocus}
        filter={filters.approvalStatusFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
      />
    ),
    creatorFilter: (
      <CreatorFilter
        autoFocus={autoFocus}
        filter={filters.creatorFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueAdd={onFilterValueAdd}
        onFilterValueRemove={onFilterValueRemove}
      />
    ),
    creationDateFilter: (
      <CreationDateFilter
        autoFocus={autoFocus}
        filter={filters.creationDateFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
        validationErrors={filters.validationErrors.creationDateFilter}
      />
    ),
    startDateFilter: (
      <StartDateFilter
        autoFocus={autoFocus}
        filter={filters.startDateFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
        validationErrors={filters.validationErrors.startDateFilter}
      />
    ),
    reviewerFilter: (
      <ReviewerFilter
        autoFocus={autoFocus}
        filter={filters.reviewerFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueAdd={onFilterValueAdd}
        onFilterValueRemove={onFilterValueRemove}
      />
    ),
    signatureStatusFilter: (
      <SignatureStatusFilter
        autoFocus={autoFocus}
        filter={filters.signatureStatusFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueChange={onFilterValueChange}
      />
    ),
    approverFilter: (
      <ApproverFilter
        autoFocus={autoFocus}
        filter={filters.approverFilter}
        onFilterRemove={onFilterRemove}
        onFilterValueAdd={onFilterValueAdd}
        onFilterValueRemove={onFilterValueRemove}
      />
    ),
  };

  seedDashboardConfigurableFields.data.forEach((dashboardConfigurableField) => {
    const filterKey = dashboardConfigurableFieldKey({
      dashboardConfigurableField,
      suffix: 'Filter',
    });

    filterComponents[filterKey] = (
      <DashboardConfigurableFieldFilter
        autoFocus={autoFocus}
        dashboardConfigurableField={dashboardConfigurableField}
        filter={filters[filterKey]}
        onFilterRemove={onFilterRemove}
        onFilterValueAdd={onFilterValueAdd}
        onFilterValueRemove={onFilterValueRemove}
      />
    );
  });

  const bodyContent = (
    <>
      {selectedVisibleFilters.map((filterKey) => (
        <div
          className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-5'
          key={filterKey}
        >
          {filterComponents[filterKey]}
        </div>
      ))}
      <div className='tw-mb-28 tw-p-5'>
        <Options
          align='left'
          btnToggle={(isOpen) => (
            <div className='tooltip-parent'>
              <OutlinedButton
                color='grey'
                disabled={isAddFilterDisabled}
                onClick={() => {}}
                size='sm'
              >
                <span>{i18nTCommon('shared.addFilter')}</span>
                <ChevronUpIcon
                  className='-tw-mb-px -tw-mr-[6px]'
                  height={24}
                  style={{ display: isOpen ? 'inline-block' : 'none' }}
                  width={24}
                />
                <ChevronDownIcon
                  className='-tw-mb-[1px] -tw-mr-[6px]'
                  height={24}
                  style={{ display: isOpen ? 'none' : 'inline-block' }}
                  width={24}
                />
              </OutlinedButton>
              {isAddFilterDisabled && (
                <Tooltip
                  placement='top'
                  tooltip={i18nTCommon('shared.allFiltersHaveBeenAdded')}
                  trigger='hover'
                />
              )}
            </div>
          )}
          buttonDisabled={isAddFilterDisabled}
          listStyle='tw-w-52 [&_li]:tw-border-none'
          toggleStyle='tw-inline-block'
        >
          {visibleFilters.map(
            (filterKey) =>
              !selectedVisibleFilters.includes(filterKey) && (
                <OptionChoice
                  key={filterKey}
                  onClick={(setOptionsOpen) => {
                    setOptionsOpen(false);
                    onFilterAdd(filterKey);
                  }}
                >
                  <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                    {baseFilters.get(filterKey)}
                  </a>
                </OptionChoice>
              ),
          )}
        </Options>
      </div>
    </>
  );

  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  return (
    <SidePanel
      bodyContent={bodyContent}
      closeCallback={closeSidePanel}
      color='tertiary'
      displayClose={false}
      headerContent={headerContent}
      isOpen={isSidePanelOpen}
    />
  );
}

ProjectsFilterSidePanel.propTypes = {
  closeSidePanel: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  isSidePanelOpen: PropTypes.bool.isRequired,
  onFilterAdd: PropTypes.func.isRequired,
  onFilterRemove: PropTypes.func.isRequired,
  onFilterValueAdd: PropTypes.func.isRequired,
  onFilterValueChange: PropTypes.func.isRequired,
  onFilterValueRemove: PropTypes.func.isRequired,
  visibleFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function StatusFilter({
  autoFocus,
  filter,
  label,
  onFilterRemove,
  onFilterValueChange,
  options,
  placeholder,
  type,
}) {
  const [isSelectable, setIsSelectable] = useState(
    !Object.values(filter).some(Boolean),
  );

  const filterKey = `${type}StatusFilter`;

  return isSelectable ?
      <SelectFilter
        autoFocus={autoFocus}
        id={`${type}_status_filter`}
        label={label}
        name={`${type}_status_filter`}
        onChange={(key) => {
          onFilterValueChange({ filterKey, key, value: true });
          setIsSelectable(false);
        }}
        onRemove={() => onFilterRemove(filterKey)}
        options={options.map((option) => ({
          value: option.key,
          label: (
            <div className='tw-flex tw-items-center'>
              <span
                className={classNames(
                  'tw-mr-1.5 tw-inline-flex tw-h-2 tw-w-2 tw-rounded-full',
                  option.className,
                )}
              />
              {option.label}
            </div>
          ),
        }))}
        placeholder={placeholder}
      />
    : <CheckboxFilter
        checkboxes={options.map((option) => ({
          label: option.label,
          name: `${type}_status_filter_${_.snakeCase(option.key)}`,
          onChange: (event) =>
            onFilterValueChange({
              filterKey,
              key: option.key,
              value: event.target.checked,
            }),
          value: filter[option.key],
        }))}
        label={label}
        minChecked={0}
        onRemove={() => onFilterRemove(filterKey)}
      />;
}

function LifecycleStatusFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
}) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.LifecycleStatusFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <StatusFilter
      autoFocus={autoFocus}
      filter={filter}
      label={i18nT('label', {
        interpolation: { escapeValue: false },
        singularRAMSName: voca.capitalize(singularRAMSName),
      })}
      onFilterRemove={onFilterRemove}
      onFilterValueChange={onFilterValueChange}
      options={[
        {
          className: 'tw-bg-green-500',
          key: 'live',
          label: i18nTShared('live'),
        },
        {
          className: 'tw-bg-amber-500',
          key: 'future',
          label: i18nTShared('future'),
        },
        {
          className: 'tw-bg-grey-400',
          key: 'ended',
          label: i18nTShared('ended'),
        },
        {
          className: 'tw-bg-grey-400',
          key: 'noDates',
          label: i18nTShared('noDates'),
        },
      ]}
      placeholder={i18nT('placeholder', {
        interpolation: { escapeValue: false },
        singularRAMSName,
      })}
      type='lifecycle'
    />
  );
}

function FolderFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueRemove,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.FolderFilter',
  });

  const { t: i18nTProjects } = useTranslation('common', {
    keyPrefix: 'projects',
  });

  return (
    <MultiSelectFilter
      autoFocus={autoFocus}
      id='folder_filter_selected_ids'
      label={i18nTProjects('folder')}
      name='folder_filter_selected_ids'
      nullResource={{ id: null, attributes: { name: i18nT('nullResource') } }}
      onDeselect={(value) => {
        onFilterValueRemove({
          filterKey: 'folderFilter',
          key: 'selectedIds',
          value,
        });
      }}
      onRemove={() => onFilterRemove('folderFilter')}
      onSelect={(value) => {
        onFilterValueAdd({
          filterKey: 'folderFilter',
          key: 'selectedIds',
          value,
        });
      }}
      placeholder={i18nT('placeholder')}
      resourceLabel={(resource) => resource.attributes.name}
      resourceSort={sortNullTopLabelsAlphabetically}
      selectedIds={filter.selectedIds}
      type='folder'
    />
  );
}

function ReviewStatusFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.ReviewStatusFilter',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <StatusFilter
      autoFocus={autoFocus}
      filter={filter}
      label={i18nTCommon('projects.reviewStatus')}
      onFilterRemove={onFilterRemove}
      onFilterValueChange={onFilterValueChange}
      options={[
        {
          className: 'tw-bg-red-600',
          key: 'pastDueDate',
          label: i18nTCommon('shared.pastDueDate'),
        },
        {
          className: 'tw-bg-amber-500',
          key: 'upcoming',
          label: i18nT('options.upcoming'),
        },
        {
          className: 'tw-bg-green-500',
          key: 'upToDate',
          label: i18nTCommon('shared.upToDate'),
        },
        {
          className: 'tw-bg-grey-400',
          key: 'notSet',
          label: i18nTCommon('shared.notSet'),
        },
      ]}
      placeholder={i18nT('placeholder')}
      type='review'
    />
  );
}

function ReviewDateFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
  validationErrors,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.ReviewDateFilter',
  });

  const { t: i18nTProjects } = useTranslation('common', {
    keyPrefix: 'projects',
  });

  return (
    <DateFilter
      autoFocus={autoFocus}
      datePeriod={filter}
      id='review_date_filter'
      label={i18nTProjects('reviewDate')}
      name='review_date_filter'
      nullPeriodOption={i18nT('nullPeriodOption')}
      onChange={(key, value) => {
        onFilterValueChange({ filterKey: 'reviewDateFilter', key, value });
      }}
      onRemove={() => onFilterRemove('reviewDateFilter')}
      validationErrors={validationErrors}
    />
  );
}

function LastUpdatedFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
  validationErrors,
}) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <DateFilter
      afterProps={{ latestDate: moment().toDate() }}
      autoFocus={autoFocus}
      beforeProps={{ latestDate: moment().toDate() }}
      customDateRangeFromProps={{ latestDate: moment().toDate() }}
      customDateRangeToProps={{ latestDate: moment().toDate() }}
      datePeriod={filter}
      id='last_updated_filter'
      label={i18nTShared('lastUpdated')}
      name='last_updated_filter'
      onChange={(key, value) => {
        onFilterValueChange({ filterKey: 'lastUpdatedFilter', key, value });
      }}
      onExactDateProps={{ latestDate: moment().toDate() }}
      onRemove={() => onFilterRemove('lastUpdatedFilter')}
      periodOptions={limitedPeriodOptions}
      validationErrors={validationErrors}
    />
  );
}

function ApprovalStatusFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.ApprovalStatusFilter',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <StatusFilter
      autoFocus={autoFocus}
      filter={filter}
      label={i18nTCommon('projects.approvalStatus')}
      onFilterRemove={onFilterRemove}
      onFilterValueChange={onFilterValueChange}
      options={[
        {
          className: 'tw-bg-red-600',
          key: 'rejected',
          label: i18nTCommon('shared.rejected'),
        },
        {
          className: 'tw-bg-amber-500',
          key: 'pendingApproval',
          label: i18nTCommon('shared.pendingApproval'),
        },
        {
          className: 'tw-bg-green-500',
          key: 'approved',
          label: i18nTCommon('shared.approved'),
        },
        {
          className: 'tw-bg-grey-400',
          key: 'notSubmitted',
          label: i18nTCommon('shared.notSubmitted'),
        },
      ]}
      placeholder={i18nT('placeholder')}
      type='approval'
    />
  );
}

function CreatorFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueRemove,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.CreatorFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <MultiSelectFilter
      autoFocus={autoFocus}
      id='creator_filter_selected_ids'
      label={i18nT('label')}
      name='creator_filter_selected_ids'
      onDeselect={(value) =>
        onFilterValueRemove({
          filterKey: 'creatorFilter',
          key: 'selectedIds',
          value,
        })
      }
      onRemove={() => onFilterRemove('creatorFilter')}
      onSelect={(value) =>
        onFilterValueAdd({
          filterKey: 'creatorFilter',
          key: 'selectedIds',
          value,
        })
      }
      placeholder={i18nT('placeholder')}
      resourceLabel={(resource) =>
        [
          personDisplayName({
            attributes: resource.attributes,
          }),
          resource.attributes.deletedAt && i18nTShared('deletedUser'),
        ]
          .filter(Boolean)
          .join(' ')
      }
      resourceSort={sortLabelsAlphabetically}
      selectedIds={filter.selectedIds}
      type='project_creator'
    />
  );
}

function CreationDateFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
  validationErrors,
}) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <DateFilter
      afterProps={{ latestDate: moment().toDate() }}
      autoFocus={autoFocus}
      beforeProps={{ latestDate: moment().toDate() }}
      customDateRangeFromProps={{ latestDate: moment().toDate() }}
      customDateRangeToProps={{ latestDate: moment().toDate() }}
      datePeriod={filter}
      id='creation_date_filter'
      label={i18nTShared('created')}
      name='creation_date_filter'
      onChange={(key, value) => {
        onFilterValueChange({ filterKey: 'creationDateFilter', key, value });
      }}
      onExactDateProps={{ latestDate: moment().toDate() }}
      onRemove={() => onFilterRemove('creationDateFilter')}
      periodOptions={limitedPeriodOptions}
      validationErrors={validationErrors}
    />
  );
}

function StartDateFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
  validationErrors,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.StartDateFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <DateFilter
      autoFocus={autoFocus}
      datePeriod={filter}
      id='start_date_filter'
      label={i18nTShared('startDate')}
      name='start_date_filter'
      nullPeriodOption={i18nT('nullPeriodOption')}
      onChange={(key, value) => {
        onFilterValueChange({ filterKey: 'startDateFilter', key, value });
      }}
      onRemove={() => onFilterRemove('startDateFilter')}
      validationErrors={validationErrors}
    />
  );
}

function ReviewerFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueRemove,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.ReviewerFilter',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <MultiSelectFilter
      autoFocus={autoFocus}
      id='reviewer_filter_selected_ids'
      label={i18nT('label')}
      name='reviewer_filter_selected_ids'
      nullResource={{
        attributes: { firstName: i18nT('nullResource') },
        id: null,
      }}
      onDeselect={(value) =>
        onFilterValueRemove({
          filterKey: 'reviewerFilter',
          key: 'selectedIds',
          value,
        })
      }
      onRemove={() => onFilterRemove('reviewerFilter')}
      onSelect={(value) =>
        onFilterValueAdd({
          filterKey: 'reviewerFilter',
          key: 'selectedIds',
          value,
        })
      }
      placeholder={i18nT('placeholder')}
      resourceLabel={(resource) =>
        [
          personDisplayName({
            attributes: resource.attributes,
          }),
          resource.attributes.deletedAt && i18nTShared('deletedUser'),
        ]
          .filter(Boolean)
          .join(' ')
      }
      resourceSort={sortNullTopLabelsAlphabetically}
      selectedIds={filter.selectedIds}
      type='project_reviewer'
    />
  );
}

function SignatureStatusFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueChange,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.SignatureStatusFilter',
  });

  const { t: i18nTProjects } = useTranslation('common', {
    keyPrefix: 'projects',
  });

  return (
    <StatusFilter
      autoFocus={autoFocus}
      filter={filter}
      label={i18nTProjects('signatureStatus')}
      onFilterRemove={onFilterRemove}
      onFilterValueChange={onFilterValueChange}
      options={[
        {
          className: 'tw-bg-red-600',
          key: 'hasRejections',
          label: i18nTProjects('hasRejections'),
        },
        {
          className: 'tw-bg-amber-500',
          key: 'pendingSignatures',
          label: i18nTProjects('pendingSignatures'),
        },
        {
          className: 'tw-bg-green-500',
          key: 'fullySigned',
          label: i18nTProjects('fullySigned'),
        },
        {
          className: 'tw-bg-grey-400',
          key: 'notRequested',
          label: i18nTProjects('notRequested'),
        },
      ]}
      placeholder={i18nT('placeholder')}
      type='signature'
    />
  );
}

function ApproverFilter({
  autoFocus,
  filter,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueRemove,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.ApproverFilter',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <MultiSelectFilter
      autoFocus={autoFocus}
      id='approver_filter_selected_ids'
      label={i18nTCommon('projects.approver')}
      name='approver_filter_selected_ids'
      nullResource={{
        id: null,
        attributes: { firstName: i18nT('nullResource') },
      }}
      onDeselect={(value) =>
        onFilterValueRemove({
          filterKey: 'approverFilter',
          key: 'selectedIds',
          value,
        })
      }
      onRemove={() => onFilterRemove('approverFilter')}
      onSelect={(value) =>
        onFilterValueAdd({
          filterKey: 'approverFilter',
          key: 'selectedIds',
          value,
        })
      }
      placeholder={i18nT('placeholder')}
      resourceLabel={(resource) =>
        [
          personDisplayName({
            attributes: resource.attributes,
          }),
          resource.attributes.deletedAt && i18nTCommon('shared.deletedUser'),
        ]
          .filter(Boolean)
          .join(' ')
      }
      resourceSort={sortNullTopLabelsAlphabetically}
      selectedIds={filter.selectedIds}
      type='project_approver'
    />
  );
}

function DashboardConfigurableFieldFilter({
  autoFocus,
  dashboardConfigurableField,
  filter,
  onFilterRemove,
  onFilterValueAdd,
  onFilterValueRemove,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsDashboard.components.ProjectsFilterSidePanel.filters.DashboardConfigurableFieldFilter',
  });

  const filterKey = dashboardConfigurableFieldKey({
    dashboardConfigurableField,
    suffix: 'Filter',
  });

  const {
    attributes: { label },
    id: fieldId,
  } = dashboardConfigurableField;

  return (
    <MultiSelectFilter
      autoFocus={autoFocus}
      id={`${voca.snakeCase(filterKey)}_selected_ids`}
      label={voca.capitalize(label)}
      name={`${voca.snakeCase(filterKey)}_selected_ids`}
      nullResource={{
        id: null,
        attributes: {
          value: i18nT('nullResource', { label: voca.capitalize(label) }),
        },
      }}
      onDeselect={(value) => {
        onFilterValueRemove({ filterKey, key: 'selectedIds', value });
      }}
      onRemove={() => onFilterRemove(filterKey)}
      onSelect={(value) => {
        onFilterValueAdd({ filterKey, key: 'selectedIds', value });
      }}
      params={{ field_id: fieldId }}
      placeholder={i18nT('placeholder', { label: voca.lowerCase(label) })}
      resourceLabel={(resource) => resource.attributes.value}
      resourceSort={sortLabelsAlphabetically}
      selectedIds={filter.selectedIds}
      type='projects_dashboards/configurable_field'
    />
  );
}

function sortLabelsAlphabetically(a, b) {
  return _.lowerCase(a.label) > _.lowerCase(b.label) ? 1 : -1;
}

function sortNullTopLabelsAlphabetically(a, b) {
  if (a.record.id === null) {
    return -1;
  } else if (b.record.id === null) {
    return 1;
  } else {
    return sortLabelsAlphabetically(a, b);
  }
}

const limitedPeriodOptions = [
  'withinTheLast',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'lastQuarter',
  'thisYear',
  'lastYear',
  'today',
  'yesterday',
  'before',
  'after',
  'onExactDate',
  'customDateRange',
];
